import { type BaseAnalyticsEvent } from '../types/events'
import { sortEventQueue } from './utils'

export const TIME_WAIT_FOR_READY_PLATFORM = 500

export type AnalyticsTransformer = <E extends BaseAnalyticsEvent>(event: E) => E | null

export abstract class DniAnalytics<CustomEvent extends BaseAnalyticsEvent> {
  private _isReady: boolean
  private _eventQueue: CustomEvent[]

  constructor() {
    this._eventQueue = []
    this._isReady = false
  }

  abstract readonly config: unknown
  protected abstract initialize(): void
  protected abstract sendAnalyticsEvent(customEvent: CustomEvent): void
  protected writeEventsToPlatform(): void {
    if (!this._isReady) {
      window.setTimeout(() => this.writeEventsToPlatform(), TIME_WAIT_FOR_READY_PLATFORM)
      return
    }
    this._eventQueue.forEach((event) => {
      this.sendAnalyticsEvent(event)
    })
    this._eventQueue = []
  }

  public get isReady(): boolean {
    return this._isReady
  }

  /**
   * only for test
   */
  public get eventQueue(): CustomEvent[] {
    return [...this._eventQueue]
  }

  // Method to safely set the ready state (only internal use)
  protected setIsReady(value: boolean): void {
    this._isReady = value
  }

  public processAnalyticsEvent(customEvent: CustomEvent, transformer?: AnalyticsTransformer): void {
    const transformedEvent = transformer ? transformer(customEvent) : customEvent
    if (transformedEvent !== null) {
      this.addToEventQueue(transformedEvent)
      this.writeEventsToPlatform()
    }
  }

  protected addToEventQueue(customEvent: CustomEvent): void {
    this._eventQueue = [...this._eventQueue, customEvent].sort(sortEventQueue)
  }
}
